/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { safelyParseJson } from '../../utils/object-helpers';
import { HttpMethods, HttpResponseCodes } from '../../enums';
import { createResponse } from '../../utils/request-helpers';

import type { XhrHttpRequestInit } from './interfaces';

export class HttpRequestService {

    fetch(url: string, options: RequestInit): Promise<Response> {
        return fetch(url, options)
            .catch((error: Error) => this.handleNetworkError(error, url, options));
    }

    xhr(url: string, options: XhrHttpRequestInit): Promise<Response> {
        const {
            method = HttpMethods.Post,
            headers,
            body,
            progress,
            signal,
        } = options;

        return new Promise((resolve) => {
            const xhr: XMLHttpRequest = new XMLHttpRequest();

            xhr.open(method, url, true);
            xhr.onloadend = () => {
                const response: any = xhr.status === HttpResponseCodes.NoContent
                    ? {}
                    : xhr.response ? safelyParseJson(xhr.response) : {};

                resolve(createResponse(xhr.status, response, url, options));
            };
            xhr.onerror = () => {
                const responseData = xhr.response ? safelyParseJson(xhr.response) : {};

                resolve(createResponse(HttpResponseCodes.NoResponse, responseData, url, options));
            };

            const onAbortHandler = () => {
                const abortController = new AbortController();
                abortController.abort();

                resolve(createResponse(HttpResponseCodes.ClientClosedRequest, abortController.signal.reason, url, options));
            };

            xhr.onabort = onAbortHandler;

            if (progress) {
                xhr.upload.onprogress = progress;
            }

            if (signal) {
                signal.onabort = () => xhr.abort();
            }

            if (headers) {
                Object.entries(headers).forEach(([name, value]) => {
                    if (name.toLowerCase() !== 'content-length') {
                        xhr.setRequestHeader(name, value);
                    }
                });
            }

            if (signal?.aborted) {
                // case when upload stream paused in 429 error and will continue later, but signal was aborted
                onAbortHandler();
            } else {
                xhr.send(body as XMLHttpRequestBodyInit);
            }
        });
    }

    protected handleNetworkError(error: Error, url: string, options?: RequestInit): Promise<Response> {
        const status = error && error.name === 'AbortError' ? HttpResponseCodes.ClientClosedRequest : HttpResponseCodes.NoResponse;

        return Promise.resolve(createResponse(status, error, url, options));
    }

}
